import type { PezEffectDefApi, PhotoEffectCatalogEntryApi } from '@/api/types/photo-effects'
import {
  PHOTO_EFFECT_DEFINITIONS,
  type PhotoEffectKind,
} from '@/lib/photo-effects/photo-effect-defs'
import type { PhotoEffectDefinition } from '@/lib/photo-effects/types/photo-effect-definition'
import type { PhotoEffectId } from '@/lib/photo-effects/types/photo-effect-id'
import { photoEffectTxFromId } from '@/lib/photo-effects/photo-effect-tx'
import type { PhotoEffectRuntimeCatalog } from '@/lib/photo-effects/types/photo-effect-runtime-catalog'

function inferKind(
  entry: PhotoEffectCatalogEntryApi,
  def: PezEffectDefApi | undefined
): PhotoEffectKind {
  if (!entry.parameter) return 'binary'

  const uiLo = def?.uiLo
  const uiHi = def?.uiHi

  if (typeof uiLo === 'number' && typeof uiHi === 'number' && uiLo < 0 && uiHi > 0) {
    return 'symmetric'
  }

  return 'percent'
}

function sentenceCaseFromApi(displayName: string, fallback: string): string {
  const trimmed = displayName.trim()

  if (!trimmed) return fallback

  // API uses Title Case; UI uses sentence case for most labels (see WMPCM-9470).
  if (trimmed === 'Improve - Indoor') return 'Improve - indoor'
  if (trimmed === 'Improve - Outdoor') return 'Improve - outdoor'
  if (trimmed === 'Fill Light') return 'Fill light'
  if (trimmed === 'Oil Painting') return 'Oil painting'
  if (trimmed === 'Red Eye') return 'Red eye'
  if (trimmed === 'Auto Brightness') return 'Auto brightness'
  if (trimmed === 'Auto Contrast') return 'Auto contrast'
  if (trimmed === 'Auto Color') return 'Auto color'

  return trimmed.charAt(0) + trimmed.slice(1)
}

function mergeDefinitionWithApi(
  local: PhotoEffectDefinition,
  entry: PhotoEffectCatalogEntryApi | undefined
): PhotoEffectDefinition {
  if (!entry) return local

  const pezDef = entry.pezEffectDef
  const kind = inferKind(entry, pezDef)

  if (kind === 'binary') {
    return {
      ...local,
      label: sentenceCaseFromApi(entry.displayName, local.label),
      kind: 'binary',
    }
  }

  const min = pezDef?.uiLo ?? local.min ?? 0
  const max = pezDef?.uiHi ?? local.max ?? 100
  const defaultValue = pezDef?.uiDefault ?? local.defaultValue ?? min

  return {
    ...local,
    label: sentenceCaseFromApi(entry.displayName, local.label),
    kind,
    min,
    max,
    defaultValue,
  }
}

export function buildPhotoEffectRuntimeCatalog(
  apiEntries: PhotoEffectCatalogEntryApi[] | null | undefined
): PhotoEffectRuntimeCatalog {
  const byTx = new Map<string, PhotoEffectCatalogEntryApi>()

  for (const entry of apiEntries ?? []) {
    if (entry?.effectName) byTx.set(entry.effectName, entry)
  }

  const pezDefById = new Map<PhotoEffectId, PezEffectDefApi>()
  const txById = new Map<PhotoEffectId, string>()
  const definitions: PhotoEffectDefinition[] = []
  let matched = 0

  for (const local of PHOTO_EFFECT_DEFINITIONS) {
    const tx = photoEffectTxFromId(local.id)
    const entry = byTx.get(tx)

    if (entry) {
      matched += 1
      txById.set(local.id, tx)

      if (entry.pezEffectDef) {
        pezDefById.set(local.id, entry.pezEffectDef)
      }
    } else {
      txById.set(local.id, tx)
    }

    definitions.push(mergeDefinitionWithApi(local, entry))
  }

  return {
    definitions,
    pezDefById,
    txById,
    fromApi: matched === PHOTO_EFFECT_DEFINITIONS.length && (apiEntries?.length ?? 0) > 0,
  }
}

export function buildFallbackPhotoEffectRuntimeCatalog(): PhotoEffectRuntimeCatalog {
  return buildPhotoEffectRuntimeCatalog(null)
}

export function normalizePhotoEffectsCatalogPayload(
  payload: unknown
): PhotoEffectCatalogEntryApi[] {
  if (!Array.isArray(payload)) return []

  const out: PhotoEffectCatalogEntryApi[] = []

  for (const item of payload) {
    if (!item || typeof item !== 'object') continue
    const record = item as Record<string, unknown>
    const effectName = typeof record.effectName === 'string' ? record.effectName : ''
    const displayName = typeof record.displayName === 'string' ? record.displayName : ''

    if (!effectName || !displayName) continue

    const parameter =
      typeof record.parameter === 'boolean'
        ? record.parameter
        : record.parameter == null
          ? false
          : Boolean(record.parameter)

    const pezEffectDef =
      record.pezEffectDef && typeof record.pezEffectDef === 'object'
        ? (record.pezEffectDef as PezEffectDefApi)
        : undefined

    out.push({ effectName, displayName, parameter, pezEffectDef })
  }

  return out
}
